import {CreateOrderParams, CreateOrderResponse} from "../types/order";
import {extractCity, extractCoordinates, formatUseCarTime} from "../utils/createOrderHelper";
import {API_HEADER} from "../utils/Constants";

const DEFAULT_TRANSIT = "SHENZHEN_PORT"; // Consider making this configurable
// Custom error class for better error handling
class OrderServiceError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = "OrderServiceError";
  }
}

export const createOrder = async ({
                                    pickupTime,
                                    passengers,
                                    luggage,
                                    rideOption,
                                    phoneNumber,
                                    countryPrefix,
                                    routeData,
                                    loginToken,
                                    serviceToken,
                                  }: CreateOrderParams): Promise<CreateOrderResponse> => {
  try {
    // Validate required parameters
    if (!phoneNumber || !countryPrefix || !loginToken || !serviceToken) {
      throw new OrderServiceError("Missing required authentication parameters");
    }

    if (!routeData || !routeData.location || !routeData.destination) {
      throw new OrderServiceError("Invalid or missing route data");
    }

    // Extract and prepare data
    const startCity = routeData.location.district
      ? extractCity(routeData.location.district)
      : null;
    const endCity = routeData.destination.district
      ? extractCity(routeData.destination.district)
      : null;
    const startAddress = `${routeData.location.district ?? ""}${routeData.location.address ?? ""}`;
    const endAddress = `${routeData.destination.district ?? ""}${routeData.destination.address ?? ""}`;

    const [startLng, startLat] = extractCoordinates(routeData.location.location) || [null, null];
    const [endLng, endLat] = extractCoordinates(routeData.destination.location) || [null, null];

    // Validate coordinates
    if (!startLat || !startLng || !endLat || !endLng) {
      throw new OrderServiceError("Invalid or missing coordinates");
    }

    // Prepare request body
    const body = {
      startCity,
      startAddress,
      startLat,
      startLng,
      endCity,
      endAddress,
      endLat,
      endLng,
      mileage: 5, // Consider making this dynamic if needed
      useCarTime: formatUseCarTime(pickupTime),
      passengers,
      luggage,
      transit: DEFAULT_TRANSIT, // Consider making this configurable
      type: "E_HAILING", // Consider making this configurable or enum
    };

    // console.log("Query params: ", body);

    // Construct URL with query parameters
    const url = new URL(`${API_HEADER}/api/order/create`);
    url.searchParams.append("languageCode", "8");
    url.searchParams.append("prefix", countryPrefix);
    url.searchParams.append("phone", phoneNumber);

    // Make API request
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": loginToken,
        "X-Csrf-Token": serviceToken,
      },
      body: JSON.stringify(body),
    });

    // Parse response
    const data: CreateOrderResponse = await response.json();

    // Handle non-OK responses
    if (!response.ok) {
      throw new OrderServiceError(
        data.message || `HTTP error! Status: ${response.status}`,
        response.status
      );
    }

    // console.log("Success:", data);
    return data;

  } catch (error) {
    // Handle and log errors
    if (error instanceof OrderServiceError) {
      console.error(`OrderServiceError: ${error.message}`, { status: error.status });
      throw error; // Re-throw custom errors
    } else if (error instanceof Error) {
      console.error("Unexpected error creating order:", error.message);
      throw new OrderServiceError("Failed to create order due to an unexpected error");
    } else {
      console.error("Unknown error:", error);
      throw new OrderServiceError("An unknown error occurred");
    }
  }
};

interface getRouteCostPayLoad {
  orderDateTime: string;
  mileage: number;
}

export const getRouteCost = async ({
  orderDateTime,
  mileage,
}: getRouteCostPayLoad) => {
  try {
    const res = await fetch(
      `${API_HEADER}/route/get-route-cost?mileage=${mileage}&orderDateTime=${orderDateTime}`,
    )
    const data = await res.json();
    return data.result;
  } catch (e) {
    console.error(e);
  }
}

export const getOrderDetail = async( orderId: string ) => {
  try {
    const res = await fetch(
      `${API_HEADER}/order/get/detail?orderId=${orderId}`,
    );
    const data = await res.json();
    // console.log(data);
    return data.result;
  } catch (e) {
    console.error(e);
  }
}